import React, { useState, FormEvent, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import '../styles/Checkout.css';
import Footer from '../components/Footer';

import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase';
import { loadStripe } from '@stripe/stripe-js';

import toast, { Toaster } from 'react-hot-toast';

// Utility Functions
const showNotification = (
  message: string,
  type: 'success' | 'error' | 'info'
) => {
  console.log(
    `[${type.toUpperCase()}] ${new Date().toISOString()} - ${message}`
  );
  toast(`${type.toUpperCase()}: ${message}`);
};

const formatCardNumber = (value: string): string => {
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  const matches = v.match(/\d{4,16}/g);
  const match = (matches && matches[0]) || '';
  const parts = [];
  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }
  return parts.length ? parts.join(' ') : value;
};

const formatUSPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  // Format as XXX-XXX-XXXX
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// Types and Interfaces
interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  category?: string;
  featured?: boolean;
  stock?: number;
  details?: string[];
  size?: string;
  color?: string;
}

interface CartItem {
  product: Product;
  quantity: number;
}

interface CheckoutFormData {
  email: string;
  confirmEmail: string;
  createAccount: boolean;
  firstName: string;
  lastName: string;
  company: string;
  address: string;
  suite: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  updates: boolean;
  shipMerchandise: boolean;
}

// Configuration
const PLACEHOLDER_IMAGE = 'https://placehold.co/150';
const SHIPPING_COST = 10;

// initializes Stripe
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);
// Main Component
const Checkout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [cart, setCart] = useState<CartItem[]>(() => initializeCart());
  const [formData, setFormData] = useState<CheckoutFormData>({
    email: '',
    confirmEmail: '',
    createAccount: false,
    firstName: '',
    lastName: '',
    company: '',
    address: '',
    suite: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    updates: false,
    shipMerchandise: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [isFormDirty, setIsFormDirty] = useState(false);

  useEffect(() => {
    const updatedCart = cart.map((item) => ({
      ...item,
      product: {
        ...item.product,
        weight: item.product.category === 'ticket' ? 0 : 1,
      },
    }));
    setCart(updatedCart);
  }, []);

  const hasMerchandise = cart.some(
    (item) => item.product.category !== 'ticket'
  );
  const shippingCost =
    hasMerchandise && formData.shipMerchandise ? SHIPPING_COST : 0;
  const subtotal = cart.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const cartTotal = subtotal + shippingCost;
  const cartCount = cart.reduce((total, item) => total + item.quantity, 0);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue =
          'You have unsaved changes. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isFormDirty]);

  function initializeCart(): CartItem[] {
    try {
      const stateCart = (location.state as { cart?: CartItem[] })?.cart;
      console.log('Initializing cart from location state:', stateCart);

      if (stateCart && Array.isArray(stateCart) && stateCart.length > 0) {
        const validCart = stateCart.filter(
          (item) =>
            item &&
            item.product &&
            typeof item.product.id !== 'undefined' &&
            typeof item.quantity === 'number'
        );

        localStorage.setItem('vipersCart', JSON.stringify(validCart));
        return validCart;
      }

      const savedCart = localStorage.getItem('vipersCart');
      if (!savedCart) return [];

      const parsedCart = JSON.parse(savedCart);
      return Array.isArray(parsedCart) ? parsedCart : [];
    } catch (e) {
      console.error('Error initializing cart:', e);
      showNotification('Failed to load cart data.', 'error');
      return [];
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    console.log(
      `Form field changed - ${name}: ${type === 'checkbox' ? checked : value}`
    );
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setIsFormDirty(true);
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatUSPhoneNumber(e.target.value);
    setFormData((prev) => ({ ...prev, phone: formatted }));
    setIsFormDirty(true);
    if (validationErrors.phone) {
      setValidationErrors((prev) => ({ ...prev, phone: '' }));
    }
  };

  const validateForm = (): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    console.log('Validating form data:', { ...formData, cardNumber: '****' });

    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = 'Email format is invalid';
    if (!formData.confirmEmail.trim())
      errors.confirmEmail = 'Confirm email is required';
    else if (formData.email !== formData.confirmEmail)
      errors.confirmEmail = 'Emails must match';
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!formData.address.trim()) errors.address = 'Street address is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.state.trim()) errors.state = 'State is required';
    else if (formData.state.length !== 2)
      errors.state = 'State must be 2 characters';
    if (!formData.zip.trim()) errors.zip = 'ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(formData.zip))
      errors.zip = 'ZIP code must be 5 or 9 digits';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^\d{3}-\d{3}-\d{4}$/.test(formData.phone))
      errors.phone = 'Phone must be in XXX-XXX-XXXX format';

    setValidationErrors(errors);
    console.log('Validation errors:', errors);
    return errors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      return;
    }
    if (cart.length === 0) {
      setError('Cart is empty.');
      showNotification('Cart is empty', 'error');
      return;
    }

    setIsFormDirty(false);

    setIsLoading(true);
    setError(null);

    const hasMerchandiseToShip = hasMerchandise && formData.shipMerchandise;
    const customerId = `customer_${Date.now()}`;
    const invoiceNumber = String(Date.now());

    const orderData = {
      // Basic info
      oId: invoiceNumber,
      cId: customerId,
      date: new Date().toISOString(),
      status: 1,

      // Billing info
      bill: {
        fn: formData.firstName,
        ln: formData.lastName,
        co: formData.company || '',
        addr: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: 'US',
        phone: formData.phone,
        email: formData.email,
      },

      // Shipping info (only if needed)
      ...(hasMerchandiseToShip && {
        ship: {
          addr: formData.address,
          city: formData.city,
          state: formData.state.toUpperCase(),
          country: 'US',
          zip: formData.zip,
          method: 1,
          cost: SHIPPING_COST,
        },
      }),

      // Items (minimized)
      items: cart.map((item) => ({
        id: item.product.id,
        n: item.product.name,
        p: item.product.price,
        q: item.quantity,
        s: item.product.size || '',
        c: item.product.color || '',
        cat: item.product.category || 'merchandise',
      })),

      // Financial info
      fin: {
        sub: subtotal,
        ship: hasMerchandiseToShip ? SHIPPING_COST : 0,
        tot: cartTotal,
      },
    };

    console.log(
      '----------------------------------orderData----------------------------------',
      orderData
    );
    // Prepare data for success page
    const successPageData = {
      name: `${formData.firstName} ${formData.lastName}`,
      email: formData.email,
      addr: formData.address,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      total: cartTotal,
      items: cart.map((item) => ({
        n: item.product.name,
        q: item.quantity,
        p: item.product.price,
      })),
    };

    const functions = getFunctions(app);
    const createCheckoutSession = httpsCallable(
      functions,
      'createCheckoutSession'
    );
    try {
      const result: any = await createCheckoutSession({
        orderData,
        successUrl: `${
          window.location.origin
        }/checkout/success?session_id={CHECKOUT_SESSION_ID}&orderData=${encodeURIComponent(
          JSON.stringify(successPageData)
        )}`,
        cancelUrl: `${window.location.origin}/checkout/cancel`,
      });

      const sessionId = result.data.sessionId;
      const stripe = await stripePromise;

      if (!stripe) {
        throw new Error('Stripe failed to initialize');
      }

      console.log(
        '----------------------------------sessionId----------------------------------',
        sessionId
      );
      const redirectResult = await stripe.redirectToCheckout({
        sessionId,
      });

      if (redirectResult.error) {
        console.error('Stripe checkout error:', redirectResult.error);
        showNotification('Payment failed. Please try again.', 'error');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      showNotification(
        'An error occurred during checkout. Please try again.',
        'error'
      );
      setIsLoading(false);
    }
  };

  const socialLinks = {
    facebook: 'https://facebook.com/vipersmhl',
    twitter: 'https://twitter.com/vipersmhl',
    instagram: 'https://instagram.com/vipersmhl',
    youtube: 'https://youtube.com/vipersmhl',
  };

  const handleShare = async () => {
    console.log('Attempting to share checkout page');
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Sip'N'Security Checkout",
          text: "Complete your purchase for Sip'N'Security tickets or merchandise!",
          url: window.location.href,
        });
        console.log('Share successful');
      } catch (err) {
        console.error('Share failed:', err);
        showNotification('Sharing failed.', 'error');
      }
    } else {
      showNotification('Share feature not supported.', 'info');
    }
  };

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h1>Checkout</h1>
        {cart.length > 0 ? (
          <>
            <section className="cart-summary">
              <h2>Your Cart ({cartCount})</h2>
              <div className="cart-items">
                {cart.map((item, index) => (
                  <div key={index} className="cart-item">
                    <img
                      src={item.product.imageUrl}
                      alt={item.product.name}
                      className="cart-item-image"
                      onError={(e) => {
                        console.log(
                          `Image load failed for ${e.currentTarget.src}, using fallback`
                        );
                        e.currentTarget.src = PLACEHOLDER_IMAGE;
                        e.currentTarget.onerror = null;
                      }}
                    />
                    <div className="cart-item-details">
                      <h4>
                        {item.product.name}
                        {item.product.category === 'ticket' ? ' (Ticket)' : ''}
                      </h4>
                      <p>Qty: {item.quantity}</p>
                      <p>
                        Price: $
                        {(item.product.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="cart-total">
                <span>Subtotal:</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {shippingCost > 0 && (
                <div className="cart-total">
                  <span>Shipping:</span>
                  <span>${shippingCost.toFixed(2)}</span>
                </div>
              )}
              <div className="cart-total grand-total">
                <span>Grand Total:</span>
                <span>${cartTotal.toFixed(2)}</span>
              </div>
            </section>

            <section className="checkout-form">
              <h2>Billing & Payment Details</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-section">
                  <h3>Contact Information</h3>
                  <div className="form-group">
                    <label htmlFor="email">Email:</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={validationErrors.email ? 'error' : ''}
                      required
                    />
                    {validationErrors.email && (
                      <div className="error-message">
                        {validationErrors.email}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirmEmail">Confirm Email:</label>
                    <input
                      type="email"
                      id="confirmEmail"
                      name="confirmEmail"
                      value={formData.confirmEmail}
                      onChange={handleChange}
                      className={validationErrors.confirmEmail ? 'error' : ''}
                      required
                    />
                    {validationErrors.confirmEmail && (
                      <div className="error-message">
                        {validationErrors.confirmEmail}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-section">
                  <h3>Billing Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name:</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={validationErrors.firstName ? 'error' : ''}
                        required
                      />
                      {validationErrors.firstName && (
                        <div className="error-message">
                          {validationErrors.firstName}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name:</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={validationErrors.lastName ? 'error' : ''}
                        required
                      />
                      {validationErrors.lastName && (
                        <div className="error-message">
                          {validationErrors.lastName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="company">Company (optional):</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="address">Address:</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={validationErrors.address ? 'error' : ''}
                      required
                    />
                    {validationErrors.address && (
                      <div className="error-message">
                        {validationErrors.address}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="suite">Suite / Apt # (optional):</label>
                    <input
                      type="text"
                      id="suite"
                      name="suite"
                      value={formData.suite}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">City:</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={validationErrors.city ? 'error' : ''}
                        required
                      />
                      {validationErrors.city && (
                        <div className="error-message">
                          {validationErrors.city}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="state">State (2 letters):</label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className={validationErrors.state ? 'error' : ''}
                        maxLength={2}
                        required
                      />
                      {validationErrors.state && (
                        <div className="error-message">
                          {validationErrors.state}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="zip">Zip Code:</label>
                      <input
                        type="text"
                        id="zip"
                        name="zip"
                        value={formData.zip}
                        onChange={handleChange}
                        className={validationErrors.zip ? 'error' : ''}
                        maxLength={10}
                        required
                      />
                      {validationErrors.zip && (
                        <div className="error-message">
                          {validationErrors.zip}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number:</label>
                    <input
                      type="text"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className={validationErrors.phone ? 'error' : ''}
                      placeholder="XXX-XXX-XXXX"
                      maxLength={12}
                      required
                    />
                    {validationErrors.phone && (
                      <div className="error-message">
                        {validationErrors.phone}
                      </div>
                    )}
                  </div>
                </div>

                {hasMerchandise && (
                  <div className="form-section">
                    <h3>Shipping Preference</h3>
                    <label>
                      <input
                        type="checkbox"
                        name="shipMerchandise"
                        checked={formData.shipMerchandise}
                        onChange={handleChange}
                        style={{
                          width: '16px',
                          height: '16px',
                          margin: 0,
                          marginRight: '12px',
                          cursor: 'pointer',
                          accentColor: '#3b82f6',
                          verticalAlign: 'middle',
                          border: '2px solid #e2e8f0',
                          borderRadius: '4px',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          top: '-1.5px',
                        }}
                      />
                      Ship my merchandise items ($10 shipping fee)
                    </label>
                  </div>
                )}

                {error && <div className="error-box">{error}</div>}

                <div className="form-actions">
                  <button
                    type="button"
                    className="back-btn"
                    onClick={() => navigate('/store')}
                  >
                    Back to Merchandise
                  </button>
                  <button
                    type="submit"
                    className="checkout-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="loading-spinner"></span>{' '}
                        "Processing..."
                      </>
                    ) : (
                      'Complete Purchase'
                    )}
                  </button>
                </div>
              </form>
            </section>
          </>
        ) : (
          <div className="empty-cart">
            <p>
              Your cart is empty.{' '}
              <Link to="/merchandise">Go back to merchandise</Link>
            </p>
          </div>
        )}
      </div>
      <Toaster />
      <Footer />
    </div>
  );
};

export default Checkout;
